<div class="companion-chat-panel">
  @let messages = messages$ | async;
  @let isLoading = isLoading$ | async;
  @let isInputDisabled = isInputDisabled$ | async;
  @let isNewConversation = isNewConversation$ | async;

  @if (splitScreen && !isChatCollapsed && !isNewConversation) {
    <div class="companion-chat-panel-actions-left">
      <button
        class="companion-chat-new-chat"
        type="button"
        [attr.aria-label]="TranslationKeys.CompanionChatPageHistoryNewChat | translate"
        [disabled]="isSplitScreenDisabled$ | async"
        (click)="startNewChat()"
      >
        <i [nimbusIcon]="ICONS.ADD"></i>
      </button>
    </div>
  }

  @if (splitScreen || (isSplitScreenFeatureEnabled$ | async)) {
    <div class="companion-chat-panel-actions">
      @if (splitScreen) {
        <button nimbusPrimaryIconButton type="button" [attr.aria-label]="toggleCollapseAriaLabel | translate" (click)="toggleCollapse()">
          <i [nimbusIcon]="isChatCollapsed ? ICONS.ANGLE_DOUBLE_LEFT : ICONS.ANGLE_DOUBLE_RIGHT"></i>
        </button>
      }
      @if (!isChatCollapsed) {
        <button
          nimbusPrimaryIconButton
          type="button"
          [attr.aria-label]="toggleSplitScreenAriaLabel | translate"
          [disabled]="isSplitScreenDisabled$ | async"
          (click)="toggleSplitScreenMode()"
        >
          <i [nimbusIcon]="splitScreen ? ICONS.EXPAND : ICONS.COLUMNS"></i>
        </button>
        @if (splitScreen) {
          <button
            nimbusPrimaryIconButton
            type="button"
            [attr.aria-label]="TranslationKeys.GeneralClose | translate"
            (click)="closeSplitScreen()"
          >
            <i [nimbusIcon]="ICONS.CLOSE_LIGHT"></i>
          </button>
        }
      }
    </div>
  }

  @if (!isChatCollapsed) {
    @if (isLoading) {
      <nimbus-spinner></nimbus-spinner>
    } @else {
      <nimbus-chat-widget
        [messages]="messages"
        [emptyTemplate]="emptyTemplate"
        [authorInfoTemplate]="authorInfoTemplate"
        [messageExtensionTemplate]="messageExtensionTemplate"
        [messageActionTemplate]="referenceLinkTemplate"
        [showActions]="true"
        [hideAuthorTime]="true"
        [hideInput]="messages?.length === 0"
        [hasMoreMessages]="hasMoreMessages"
        [inputDisabled]="isInputDisabled"
        [hasPendingAction]="hasPendingAction$ | async"
        [pendingActionHint]="TranslationKeys.ChatWidgetFormPendingHint"
        [isCustomerChat]="false"
        [showStopButton]="isAwaitingAgentMessage$ | async"
        (messageSent)="onMessageSent($event)"
        (loadPrevMessagesRequested)="loadMore()"
        (stopRequested)="onCancelAgentMessage()"
      ></nimbus-chat-widget>
      @if (messages?.length) {
        <span class="companion-chat-panel-disclaimer">
          {{ TranslationKeys.CompanionChatAiDisclaimer | translate }}
        </span>
      }
    }
  }
</div>

<ng-template #referenceLinkTemplate let-message="message">
  @if (message.referenceLinks | async; as links) {
    @for (link of links; track $index) {
      <span
        class="message-action-icon"
        [title]="link.tooltip | translate"
        [attr.aria-label]="link.tooltip | translate"
        (click)="onFormActionInvoked(link)"
      >
        <i [nimbusIcon]="link.icon"></i>
      </span>
    }
  }
</ng-template>

<ng-template #messageExtensionTemplate let-message="message">
  @if (message.form) {
    <div class="companion-chat-message-form" [class.resolved]="message.form.values !== undefined">
      <nimbus-chat-message-form
        [chatForm]="message.form"
        [formResolver]="message.formResolver"
        (resolved)="onFormResolved({ message: message, values: $event.values, comment: $event.comment })"
        (formActionInvoked)="onFormActionInvoked($event)"
      ></nimbus-chat-message-form>
    </div>
  }
  @if (message.chartType && message.chartData$) {
    <div class="companion-chat-message-chart">
      @switch (message.chartType) {
        @case (ChatChartType.Pie) {
          <nimbus-chat-message-pie [data$]="message.chartData$"></nimbus-chat-message-pie>
        }
      }
    </div>
  }
</ng-template>

<ng-template #authorInfoTemplate let-message="message">
  @let sources = getAuthorInfo(message);
  @if (sources?.length) {
    <div class="companion-chat-author-info">
      <div class="companion-chat-author-info-section">
        {{ TranslationKeys.CompanionChatInfoTopSources | translate }}
        <ul class="companion-chat-author-info-list">
          @for (source of sources; track source.url) {
            <li>
              <a [href]="source.url" target="_blank" rel="noopener">{{ source.title }}</a>
            </li>
          }
        </ul>
      </div>
    </div>
  }
</ng-template>

<ng-template #emptyTemplate>
  <div class="companion-chat-panel-empty">
    <div class="companion-chat-panel-empty-greeting">
      <i class="companion-chat-panel-empty-greeting-icon" [nimbusIcon]="ICONS.COMPANION_GREETING"></i>
      <div class="companion-chat-panel-empty-greeting-text">
        <span class="companion-chat-panel-empty-greeting-name">
          {{ TranslationKeys.CompanionChatWelcomeMessage | translate: { userName: userName } }}
        </span>
        <span class="companion-chat-panel-empty-greeting-question">
          {{ TranslationKeys.CompanionChatWelcomeQuestion | translate }}
        </span>
      </div>
    </div>

    <nimbus-chat-input-message
      [inputPlaceholderTKey]="TranslationKeys.CompanionChatMessagePlaceholder"
      [inputDisabled]="isInputDisabled"
      [isCustomerChat]="false"
      [showStopButton]="isAwaitingAgentMessage$ | async"
      (messageSent)="onMessageSent($event)"
      (stopRequested)="onCancelAgentMessage()"
    ></nimbus-chat-input-message>

    <div class="companion-chat-panel-empty-suggestions">
      @for (suggestion of suggestions; track suggestion.text) {
        @let text = suggestion.text | translate;
        <button class="companion-chat-panel-empty-suggestion-card" (click)="onMessageSent(text)">
          <span class="companion-chat-panel-empty-suggestion-category">{{ suggestion.category | translate }}</span>
          <div class="companion-chat-panel-empty-suggestion-body">
            <i [nimbusIcon]="ICONS.CHECK_CIRCLE" class="companion-chat-panel-empty-suggestion-icon"></i>
            <span class="companion-chat-panel-empty-suggestion-text tooltip-container">
              {{ suggestion.text | translate }}
              <nimbus-tooltip [text]="text"></nimbus-tooltip>
            </span>
          </div>
        </button>
      }
    </div>

    <span class="companion-chat-panel-empty-disclaimer">
      {{ TranslationKeys.CompanionChatAiDisclaimer | translate }}
    </span>
  </div>
</ng-template>
